import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import {
  ManagedUserActivity,
  MeResponse,
  Organisation,
  OrganisationRole,
} from '../../types';
import { formatTimeAgo } from '../../utils/time';
import Navbar from '../shared/Navbar';
import StudentProgressDetailModal, { ProgressDetail } from './StudentProgressDetailModal';

const formatDate = (value: string) => new Date(value).toLocaleString();

const ManageUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId: userIdParam } = useParams();
  const userId = Number(userIdParam);
  const validUserId = Number.isInteger(userId) && userId > 0;
  const [activity, setActivity] = useState<ManagedUserActivity | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<number | null>(null);
  const [selectedOrganisationRole, setSelectedOrganisationRole] = useState<OrganisationRole>('STUDENT');
  const [loading, setLoading] = useState(true);
  const [adminBusy, setAdminBusy] = useState(false);
  const [organisationBusy, setOrganisationBusy] = useState(false);
  const [detail, setDetail] = useState<ProgressDetail | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadActivity = async () => {
      if (!validUserId) {
        setError('Invalid user');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const [response, current] = await Promise.all([
          api.getManagedUserActivity(userId),
          api.me(),
        ]);
        setActivity(response);
        setMe(current);
        setSelectedOrganisationId(response.user.organisation_id);
        setSelectedOrganisationRole(response.user.organisation_role ?? 'STUDENT');
        if (current.authenticated && current.user.is_admin) {
          const organisationResponse = await api.getOrganisations();
          setOrganisations(organisationResponse.organisations);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load user activity');
      } finally {
        setLoading(false);
      }
    };
    void loadActivity();
  }, [userId, validUserId]);

  const items = useMemo(() => {
    if (!activity) return [];
    return [
      ...activity.chats.map(chat => ({
        kind: 'chat' as const,
        id: chat.id,
        lastActivityAt: chat.last_activity_at,
        chat,
      })),
      ...activity.exam_attempts.map(attempt => ({
        kind: 'exam' as const,
        id: attempt.id,
        lastActivityAt: attempt.last_activity_at,
        attempt,
      })),
    ].sort(
      (first, second) =>
        new Date(second.lastActivityAt).getTime() - new Date(first.lastActivityAt).getTime(),
    );
  }, [activity]);

  const isGlobalAdmin = Boolean(me?.authenticated && me.user.is_admin);

  const handleToggleAdmin = async () => {
    if (!activity || !isGlobalAdmin) return;
    try {
      setAdminBusy(true);
      setError(null);
      const response = await api.setUserAdmin(userId, !activity.user.is_admin);
      setActivity(current => current
        ? {
            ...current,
            user: { ...current.user, is_admin: Boolean(response.is_admin) },
          }
        : current);
    } catch (err: any) {
      setError(err?.message || 'Failed to update admin status');
    } finally {
      setAdminBusy(false);
    }
  };

  const handleSaveOrganisation = async (event: FormEvent) => {
    event.preventDefault();
    if (!activity || !isGlobalAdmin) return;
    try {
      setOrganisationBusy(true);
      setError(null);
      const response = await api.setUserOrganisation(
        userId,
        selectedOrganisationId,
        selectedOrganisationId === null ? null : selectedOrganisationRole,
      );
      setActivity(current => current
        ? {
            ...current,
            user: {
              ...current.user,
              organisation_id: response.user.organisation_id,
              organisation_name: response.user.organisation_name,
              organisation_role: response.user.organisation_role,
            },
          }
        : current);
    } catch (err: any) {
      setError(err?.message || 'Failed to update organisation');
    } finally {
      setOrganisationBusy(false);
    }
  };

  const openActivityDetail = async (
    activityType: 'chat' | 'exam',
    activityId: string,
  ) => {
    try {
      setDetailLoadingId(activityId);
      setError(null);
      setDetail(await api.getManagedUserActivityDetail(
        userId,
        activityType,
        activityId,
      ));
    } catch (err: any) {
      setError(err?.message || 'Failed to load activity preview');
    } finally {
      setDetailLoadingId(null);
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container student-activity-page">
          <div className="student-activity-heading">
            <div className="page-intro">
              <span className="class-progress-eyebrow">User activity</span>
              <h2>{activity?.user.email ?? 'User activity'}</h2>
              <p>
                {activity?.user.organisation_id && activity.user.organisation_name ? (
                  <>
                    <Link
                      className="mu-organisation-summary"
                      to={`/manage/${activity.user.organisation_id}`}
                    >
                      {activity.user.organisation_name}
                    </Link>
                    {' · Lesson chats and exam attempts'}
                  </>
                ) : (
                  'Lesson chats and exam attempts'
                )}
              </p>
            </div>
            <button type="button" className="tm-button tm-button--secondary org-secondary-btn" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>

          {error && <div className="org-error" role="alert">{error}</div>}
          {loading && <div className="loading">Loading user activity…</div>}

          {activity && isGlobalAdmin && (
            <section className="tm-surface manage-user-controls">
              <header>
                <div>
                  <h3>Account settings</h3>
                  <span>Global admin controls</span>
                </div>
                <button
                  type="button"
                  className="tm-button tm-button--primary ml-replace-btn"
                  onClick={() => void handleToggleAdmin()}
                  disabled={adminBusy}
                >
                  {adminBusy
                    ? 'Updating…'
                    : activity.user.is_admin
                    ? 'Revoke Global Admin'
                    : 'Make Global Admin'}
                </button>
              </header>
              <form onSubmit={event => void handleSaveOrganisation(event)}>
                <label>
                  Organisation
                  <select
                    value={selectedOrganisationId ?? ''}
                    onChange={event => {
                      const value = event.target.value;
                      setSelectedOrganisationId(value ? Number(value) : null);
                    }}
                    disabled={organisationBusy}
                  >
                    <option value="">No organisation</option>
                    {organisations.map(organisation => (
                      <option key={organisation.id} value={organisation.id}>
                        {organisation.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Role
                  <select
                    value={selectedOrganisationRole}
                    onChange={event => setSelectedOrganisationRole(event.target.value as OrganisationRole)}
                    disabled={selectedOrganisationId === null || organisationBusy}
                  >
                    <option value="ADMIN">Admin</option>
                    <option value="TEACHER">Teacher</option>
                    <option value="STUDENT">Student</option>
                  </select>
                </label>
                <button type="submit" className="tm-button tm-button--secondary org-secondary-btn" disabled={organisationBusy}>
                  {organisationBusy ? 'Saving…' : 'Save organisation'}
                </button>
              </form>
              <p>Changing organisation removes the user from their current classes.</p>
            </section>
          )}

          {activity && (
            <section className="tm-surface student-activity-section">
              <header>
                <div>
                  <h3>Activity</h3>
                  <span>{items.length} total</span>
                </div>
              </header>
              {items.length === 0 ? (
                <div className="ml-empty">This user has no activity.</div>
              ) : (
                <div className="student-activity-list">
                  {items.map(item => item.kind === 'chat' ? (
                    <button
                      type="button"
                      className="student-activity-card"
                      key={`chat-${item.id}`}
                      onClick={() => void openActivityDetail('chat', item.id)}
                      disabled={detailLoadingId !== null}
                    >
                      <div className="student-activity-card-heading">
                        <strong>{item.chat.lesson_title || item.chat.lesson_id}</strong>
                        <span className="student-activity-type">Lesson chat</span>
                      </div>
                      <div className="student-activity-card-meta">
                        <span>{item.chat.message_count} messages</span>
                      </div>
                      <div className="student-activity-dates">
                        <span><strong>Last active:</strong> {formatTimeAgo(item.chat.last_activity_at)}</span>
                        <span title={formatDate(item.chat.created_at)}>
                          <strong>Started:</strong> {formatTimeAgo(item.chat.created_at)}
                        </span>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="student-activity-card"
                      key={`exam-${item.id}`}
                      onClick={() => void openActivityDetail('exam', item.id)}
                      disabled={detailLoadingId !== null}
                    >
                      <div className="student-activity-card-heading">
                        <strong>{item.attempt.exam_name}</strong>
                        <div className="student-activity-card-badges">
                          <span className="student-activity-type">Exam attempt</span>
                          <span className={`student-attempt-state student-attempt-state--${item.attempt.state.toLowerCase()}`}>
                            {item.attempt.state.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="student-attempt-result">
                        {item.attempt.obtained_marks !== null
                          ? `${item.attempt.obtained_marks} / ${item.attempt.total_marks ?? '—'} marks`
                          : 'No result yet'}
                      </div>
                      <div className="student-activity-dates">
                        <span><strong>Last active:</strong> {formatTimeAgo(item.attempt.last_activity_at)}</span>
                        <span title={formatDate(item.attempt.created_at)}>
                          <strong>Started:</strong> {formatTimeAgo(item.attempt.created_at)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </main>
      {detail && (
        <StudentProgressDetailModal
          detail={detail}
          isGlobalAdmin={isGlobalAdmin}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
};

export default ManageUserPage;
